import React, { useState, useEffect } from 'react';
import { Globe, ChevronDown, X, Loader2, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const TranslatePanel = ({ 
  documentType,
  documentId,
  originalContent,
  onTranslated,
  onTranslating: onTranslatingCallback,
  token,
  compact = false,
  autoTranslateLanguage = null
}) => {
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState('');
  const [translating, setTranslating] = useState(false);
  const [translated, setTranslated] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState(null);
  const autoTriggeredRef = React.useRef(false);

  useEffect(() => {
    fetchLanguages();
  }, []);

  // Auto-translate when autoTranslateLanguage is set and languages are loaded
  useEffect(() => {
    if (
      autoTranslateLanguage &&
      autoTranslateLanguage !== 'en' &&
      languages.length > 0 &&
      !autoTriggeredRef.current &&
      !translated
    ) {
      const langExists = languages.find(l => l.code === autoTranslateLanguage);
      if (langExists) {
        autoTriggeredRef.current = true;
        handleTranslate(autoTranslateLanguage);
      }
    }
  }, [autoTranslateLanguage, languages]);

  const fetchLanguages = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/translate/languages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setLanguages(data.languages);
      }
    } catch (err) {
      console.error('Failed to fetch languages:', err);
    }
  };

  const handleTranslate = async (langCode) => {
    setSelectedLang(langCode);
    setShowDropdown(false);
    setTranslating(true);
    setError(null);
    if (onTranslatingCallback) onTranslatingCallback(true);

    try {
      const body = documentId 
        ? { document_type: documentType, document_id: documentId, target_language: langCode }
        : { text: originalContent, target_language: langCode, document_type: documentType };

      const endpoint = documentId ? '/api/translate/document' : '/api/translate/text';

      const res = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await res.json();

      if (data.success) {
        const text = data.translation || data.translatedText;
        setTranslated({
          text,
          language: data.language,
          cached: data.cached
        });
        if (onTranslated) {
          onTranslated(text, langCode, data.language);
        }
      } else {
        setError(data.detail || 'Translation failed');
      }
    } catch (err) {
      setError('Translation service unavailable');
    } finally {
      setTranslating(false);
      if (onTranslatingCallback) onTranslatingCallback(false);
    }
  };

  const clearTranslation = () => {
    setTranslated(null);
    setSelectedLang('');
    if (onTranslated) {
      onTranslated(null, null, null);
    }
  };

  const selectedLanguageName = languages.find(l => l.code === selectedLang)?.name || '';

  return (
    <div data-testid="translate-panel">
      <div className="flex items-center gap-2 flex-wrap">
        {/* Language selector */}
        <div className="relative">
          <button
            data-testid="translate-language-selector"
            onClick={() => setShowDropdown(!showDropdown)}
            disabled={translating}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all
              ${translated 
                ? 'bg-green-50 border-green-300 text-green-700' 
                : 'bg-white border-gray-300 text-gray-700 hover:bg-blue-50 hover:border-blue-300'
              }
              ${translating ? 'opacity-60 cursor-wait' : 'cursor-pointer'}
            `}
          >
            <Globe className="h-4 w-4" />
            {translating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Translating...</span>
              </>
            ) : translated ? (
              <>
                <Check className="h-4 w-4" />
                <span>{selectedLanguageName}</span>
              </>
            ) : (
              <>
                <span>{compact ? 'Translate' : 'Translate to...'}</span>
                <ChevronDown className="h-3 w-3" />
              </>
            )}
          </button>

          {showDropdown && (
            <div 
              className="absolute z-50 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-64 overflow-y-auto"
              data-testid="translate-language-dropdown"
            >
              {languages.map(lang => (
                <button
                  key={lang.code}
                  data-testid={`translate-lang-${lang.code}`}
                  onClick={() => handleTranslate(lang.code)}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-blue-50 transition-colors
                    ${selectedLang === lang.code ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}
                  `}
                >
                  {lang.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Clear button */}
        {translated && (
          <button
            data-testid="translate-clear-btn"
            onClick={clearTranslation}
            className="flex items-center gap-1 px-2 py-2 text-sm text-gray-500 hover:text-red-500 transition-colors"
            title="Show original"
          >
            <X className="h-4 w-4" />
            <span className="text-xs">Original</span>
          </button>
        )}
      </div>

      {error && (
        <div className="mt-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-md" data-testid="translate-error">
          {error}
        </div>
      )}

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowDropdown(false)} 
        />
      )}
    </div>
  );
};

export default TranslatePanel;
